// react
import { SyntheticEvent, useContext, useEffect, useState } from 'react';

// router
import { useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// types
import {
	CustomColumnDefinition,
	CustomColumnType,
	CustomFieldDefinition,
	CustomFieldEntityType,
	Project,
} from '../../../../../@types';

// context
import { ContextProject } from '../../Project.context';

// mui
import {
	Alert,
	Box,
	Button,
	Chip,
	Container,
	Grid,
	IconButton,
	Paper,
	Stack,
	Switch,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tabs,
	Tooltip,
	Typography,
} from '@mui/material';

// icons
import {
	Add as IconAdd,
	ArrowDownward as IconArrowDown,
	ArrowUpward as IconArrowUp,
	Edit as IconEdit,
} from '@mui/icons-material';

// dialogs
import { DialogCustomColumn } from '../../../../dialogs/DialogCustomColumn/DialogCustomColumn';

// Types

/** Entity types that support custom fields */
type CustomFieldEntity =
	| 'activity'
	| 'beneficiary'
	| 'contract'
	| 'spendPlan'
	| 'supplier'
	| 'workPlan';

interface EntityConfig {
	/** i18n key for the tab label */
	labelKey: string;
	/** API entity type value */
	apiEntityType: CustomFieldEntityType;
	/** i18n key for the description shown below the header */
	descriptionKey: string;
}

const ENTITY_CONFIGS: Record<CustomFieldEntity, EntityConfig> = {
	activity: {
		labelKey: 'activities',
		apiEntityType: 'activity',
		descriptionKey: 'spendPlan.customFields.activity.description',
	},
	workPlan: {
		labelKey: 'workPlans',
		apiEntityType: 'work_plan',
		descriptionKey: 'spendPlan.customFields.workPlan.description',
	},
	spendPlan: {
		labelKey: 'spendPlans',
		apiEntityType: 'spend_plan',
		descriptionKey: 'spendPlan.customFields.spendPlan.description',
	},
	contract: {
		labelKey: 'contracts',
		apiEntityType: 'contract',
		descriptionKey: 'spendPlan.customFields.contract.description',
	},
	beneficiary: {
		labelKey: 'beneficiaries',
		apiEntityType: 'beneficiary',
		descriptionKey: 'spendPlan.customFields.beneficiary.description',
	},
	supplier: {
		labelKey: 'suppliers',
		apiEntityType: 'supplier',
		descriptionKey: 'spendPlan.customFields.supplier.description',
	},
};

const ENTITY_ORDER: CustomFieldEntity[] = [
	'activity',
	'workPlan',
	'spendPlan',
	'contract',
	'beneficiary',
	'supplier',
];

const getColumnsByEntityFromProject = (
	project: Project | null
): Record<CustomFieldEntity, CustomColumnDefinition[]> => {
	const definitionsByEntity = (project?.customFieldDefinitions || []).reduce<
		Partial<Record<CustomFieldEntityType, CustomColumnDefinition[]>>
	>((acc, row) => {
		acc[row.entityType] = row.definitions || [];
		return acc;
	}, {});

	return {
		activity: definitionsByEntity.activity || [],
		beneficiary: definitionsByEntity.beneficiary || [],
		contract: definitionsByEntity.contract || [],
		spendPlan: definitionsByEntity.spend_plan || [],
		supplier: definitionsByEntity.supplier || [],
		workPlan: definitionsByEntity.work_plan || [],
	};
};

// Generate a unique ID without external dependencies
const generateColumnId = () => {
	return `col_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Component

export const CustomFields = () => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();

	// context
	const { project, setProject } = useContext(ContextProject);

	// router
	const [searchParams, setSearchParams] = useSearchParams();

	// state - active entity tab (initialize from URL ?entity= param if present)
	const [activeEntity, setActiveEntity] = useState<CustomFieldEntity>(() => {
		const param = searchParams.get('entity') as CustomFieldEntity | null;
		return param && ENTITY_ORDER.includes(param) ? param : 'activity';
	});

	// Sync tab with URL param changes (e.g. back/forward navigation)
	useEffect(() => {
		const param = searchParams.get('entity') as CustomFieldEntity | null;
		if (param && ENTITY_ORDER.includes(param) && param !== activeEntity) {
			setActiveEntity(param);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams]);

	// state - columns for each entity type (initialized from project)
	const [columnsByEntity, setColumnsByEntity] = useState<
		Record<CustomFieldEntity, CustomColumnDefinition[]>
	>(() => getColumnsByEntityFromProject(project));

	useEffect(() => {
		setColumnsByEntity(getColumnsByEntityFromProject(project));
	}, [project]);

	// state - dialog
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editingColumn, setEditingColumn] = useState<CustomColumnDefinition | null>(null);

	// state - save feedback
	const [isSaving, setIsSaving] = useState(false);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [saveSuccess, setSaveSuccess] = useState(false);

	// derived
	const config = ENTITY_CONFIGS[activeEntity];
	const columns = columnsByEntity[activeEntity] ?? [];
	const activeColumns = columns.filter((col) => col.isActive);
	const inactiveColumns = columns.filter((col) => !col.isActive);

	// handlers - tab
	const handleChangeTab = (_e: SyntheticEvent, value: CustomFieldEntity) => {
		setActiveEntity(value);
		const nextParams = new URLSearchParams(searchParams);
		nextParams.set('entity', value);
		setSearchParams(nextParams);
		setSaveError(null);
		setSaveSuccess(false);
	};

	// handlers - dialog
	const handleAddColumn = () => {
		setEditingColumn(null);
		setDialogOpen(true);
	};

	const handleEditColumn = (column: CustomColumnDefinition) => {
		setEditingColumn(column);
		setDialogOpen(true);
	};

	const handleSaveColumn = (columnData: Omit<CustomColumnDefinition, 'id' | 'order'>) => {
		let updatedColumns: CustomColumnDefinition[];

		if (editingColumn) {
			updatedColumns = columns.map((col) =>
				col.id === editingColumn.id ? { ...col, ...columnData } : col
			);
		} else {
			const newColumn: CustomColumnDefinition = {
				...columnData,
				id: generateColumnId(),
				order: columns.length,
			};
			updatedColumns = [...columns, newColumn];
		}

		const updated = { ...columnsByEntity, [activeEntity]: updatedColumns };
		setColumnsByEntity(updated);
		setDialogOpen(false);
		setEditingColumn(null);
		saveColumns(activeEntity, updatedColumns);
	};

	// handlers - toggle / reorder
	const handleToggleActive = (columnId: string) => {
		const updatedColumns = columns.map((col) =>
			col.id === columnId ? { ...col, isActive: !col.isActive } : col
		);
		const updated = { ...columnsByEntity, [activeEntity]: updatedColumns };
		setColumnsByEntity(updated);
		saveColumns(activeEntity, updatedColumns);
	};

	const handleMoveUp = (index: number) => {
		if (index === 0) return;
		const updatedColumns = [...columns];
		[updatedColumns[index - 1], updatedColumns[index]] = [
			updatedColumns[index],
			updatedColumns[index - 1],
		];
		updatedColumns.forEach((col, i) => (col.order = i));
		const updated = { ...columnsByEntity, [activeEntity]: updatedColumns };
		setColumnsByEntity(updated);
		saveColumns(activeEntity, updatedColumns);
	};

	const handleMoveDown = (index: number) => {
		if (index === columns.length - 1) return;
		const updatedColumns = [...columns];
		[updatedColumns[index], updatedColumns[index + 1]] = [
			updatedColumns[index + 1],
			updatedColumns[index],
		];
		updatedColumns.forEach((col, i) => (col.order = i));
		const updated = { ...columnsByEntity, [activeEntity]: updatedColumns };
		setColumnsByEntity(updated);
		saveColumns(activeEntity, updatedColumns);
	};

	// save to backend
	const saveColumns = async (
		entity: CustomFieldEntity,
		columnsToSave: CustomColumnDefinition[]
	) => {
		if (!project) return;

		setIsSaving(true);
		setSaveError(null);
		setSaveSuccess(false);

		const { apiEntityType } = ENTITY_CONFIGS[entity];

		try {
			const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/v1\/?$/, '');
			const response = await fetch(`${apiUrl}/v2/custom-field-definitions`, {
				method: 'PATCH',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					'Content-Type': 'application/json',
					User: String(idToken),
				},
				body: JSON.stringify({
					definitions: columnsToSave,
					entityType: apiEntityType,
					projectId: project.id,
				}),
			});

			const result = await response.json();

			if (result.error) {
				setSaveError(result.error.message || 'Failed to save custom fields');
			} else {
				setSaveSuccess(true);
				// Update project context
				if (setProject && project) {
					const existingDefinitions = project.customFieldDefinitions || [];
					const nextDefinition = result.data as CustomFieldDefinition;
					const nextDefinitions = [
						...existingDefinitions.filter((d) => d.id !== nextDefinition.id),
						nextDefinition,
					];

					setProject({
						...project,
						customFieldDefinitions: nextDefinitions,
					});
				}
				setTimeout(() => setSaveSuccess(false), 3000);
			}
		} catch {
			setSaveError('Failed to save custom fields');
		} finally {
			setIsSaving(false);
		}
	};

	// helpers
	const getColumnTypeLabel = (type: CustomColumnType): string => {
		const labels: Record<CustomColumnType, string> = {
			boolean: t('spendPlan.columnType.boolean'),
			date: t('spendPlan.columnType.date'),
			number: t('spendPlan.columnType.number'),
			select: t('spendPlan.columnType.select'),
			text: t('spendPlan.columnType.text'),
		};
		return labels[type] || type;
	};

	const getColumnTypeColor = (
		type: CustomColumnType
	): 'default' | 'primary' | 'secondary' | 'success' | 'info' | 'warning' => {
		const colors: Record<
			CustomColumnType,
			'default' | 'primary' | 'secondary' | 'success' | 'info' | 'warning'
		> = {
			boolean: 'success',
			date: 'info',
			number: 'warning',
			select: 'secondary',
			text: 'primary',
		};
		return colors[type] || 'default';
	};

	// Column table row renderer
	const renderColumnRow = (column: CustomColumnDefinition, index: number, isActive: boolean) => (
		<TableRow key={column.id} hover sx={!isActive ? { opacity: 0.7 } : undefined}>
			{isActive && (
				<TableCell>
					<Stack direction="row" spacing={0.5}>
						<IconButton
							disabled={index === 0 || isSaving}
							onClick={() => handleMoveUp(columns.indexOf(column))}
							size="small">
							<IconArrowUp fontSize="small" />
						</IconButton>
						<IconButton
							disabled={index === activeColumns.length - 1 || isSaving}
							onClick={() => handleMoveDown(columns.indexOf(column))}
							size="small">
							<IconArrowDown fontSize="small" />
						</IconButton>
					</Stack>
				</TableCell>
			)}
			<TableCell>
				<Typography fontWeight={500}>{column.name}</Typography>
				{column.type === 'select' && column.options && (
					<Typography color="text.secondary" variant="caption">
						{column.options.join(', ')}
					</Typography>
				)}
			</TableCell>
			<TableCell>
				<Chip
					color={getColumnTypeColor(column.type)}
					label={getColumnTypeLabel(column.type)}
					size="small"
					variant="outlined"
				/>
			</TableCell>
			{isActive && (
				<TableCell align="center">
					{column.isRequired ? (
						<Chip color="error" label={t('yes')} size="small" />
					) : (
						<Typography color="text.secondary" variant="body2">
							{t('no')}
						</Typography>
					)}
				</TableCell>
			)}
			<TableCell align="center">
				<Switch
					checked={column.isActive}
					disabled={isSaving}
					onChange={() => handleToggleActive(column.id)}
					size="small"
				/>
			</TableCell>
			<TableCell align="center">
				<Tooltip title={t('edit')}>
					<IconButton disabled={isSaving} onClick={() => handleEditColumn(column)} size="small">
						<IconEdit fontSize="small" />
					</IconButton>
				</Tooltip>
			</TableCell>
		</TableRow>
	);

	return (
		<Container maxWidth={false}>
			<Grid container spacing={2}>
				{/* Header */}
				<Grid item xs={12}>
					<Stack alignItems="center" direction="row" justifyContent="space-between" spacing={2}>
						<Box>
							<Typography variant="h3">{t('spendPlan.customFields')}</Typography>
							<Typography color="text.secondary" variant="body2">
							{t('spendPlan.customFields.subtitle')}
							</Typography>
						</Box>
						<Button onClick={handleAddColumn} startIcon={<IconAdd />} variant="contained">
							{t('spendPlan.addField')}
						</Button>
					</Stack>
				</Grid>

				{/* Entity Type Tabs */}
				<Grid item xs={12}>
					<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
						<Tabs
							onChange={handleChangeTab}
							value={activeEntity}
							variant="scrollable"
							scrollButtons="auto">
							{ENTITY_ORDER.map((entity) => {
								const cfg = ENTITY_CONFIGS[entity];
								const count = (columnsByEntity[entity] || []).filter((c) => c.isActive).length;
								return (
									<Tab
										key={entity}
										label={
											<Stack direction="row" alignItems="center" spacing={0.5}>
												<span>{t(cfg.labelKey)}</span>
												{count > 0 && (
													<Chip
														label={count}
														size="small"
														color="primary"
														sx={{ height: 20, minWidth: 20 }}
													/>
												)}
											</Stack>
										}
										value={entity}
									/>
								);
							})}
						</Tabs>
					</Box>
				</Grid>

				{/* Entity description */}
				<Grid item xs={12}>
					<Typography color="text.secondary" variant="body2">
					{t(config.descriptionKey)}
					</Typography>
				</Grid>

				{/* Alerts */}
				{saveError && (
					<Grid item xs={12}>
						<Alert onClose={() => setSaveError(null)} severity="error">
							{saveError}
						</Alert>
					</Grid>
				)}

				{saveSuccess && (
					<Grid item xs={12}>
						<Alert severity="success">{t('spendPlan.fieldsSavedSuccessfully')}</Alert>
					</Grid>
				)}

				{/* Active Columns */}
				<Grid item xs={12}>
					<Paper sx={{ p: 2 }}>
						<Typography gutterBottom variant="h4">
							{t('spendPlan.activeFields')} ({activeColumns.length})
						</Typography>

						{activeColumns.length === 0 ? (
							<Alert severity="info">{t('spendPlan.noActiveFieldsYet')}</Alert>
						) : (
							<TableContainer>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell width={50}>{t('order')}</TableCell>
											<TableCell>{t('name')}</TableCell>
											<TableCell width={120}>{t('type')}</TableCell>
											<TableCell width={100} align="center">
												{t('required')}
											</TableCell>
											<TableCell width={100} align="center">
												{t('active')}
											</TableCell>
											<TableCell width={120} align="center">
												{t('actions')}
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{activeColumns.map((column, index) => renderColumnRow(column, index, true))}
									</TableBody>
								</Table>
							</TableContainer>
						)}
					</Paper>
				</Grid>

				{/* Inactive Columns */}
				{inactiveColumns.length > 0 && (
					<Grid item xs={12}>
						<Paper sx={{ p: 2 }}>
							<Typography gutterBottom variant="h4">
								{t('spendPlan.inactiveFields')} ({inactiveColumns.length})
							</Typography>
							<Typography color="text.secondary" gutterBottom variant="body2">
								{t('spendPlan.inactiveFieldsDescription')}
							</Typography>

							<TableContainer>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>{t('name')}</TableCell>
											<TableCell width={120}>{t('type')}</TableCell>
											<TableCell width={100} align="center">
												{t('active')}
											</TableCell>
											<TableCell width={120} align="center">
												{t('actions')}
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{inactiveColumns.map((column, index) => renderColumnRow(column, index, false))}
									</TableBody>
								</Table>
							</TableContainer>
						</Paper>
					</Grid>
				)}
			</Grid>

			{/* Add/Edit Column Dialog */}
			<DialogCustomColumn
				column={editingColumn}
				onClose={() => {
					setDialogOpen(false);
					setEditingColumn(null);
				}}
				onSave={handleSaveColumn}
				open={dialogOpen}
			/>
		</Container>
	);
};
